import React, { useMemo, useState } from 'react';
import {
	Box,
	Chip,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import {
	agencyName,
	antiguedadTooltip,
	BUCKETS_POR_VENCER,
	BUCKETS_VENCIDO,
	diferenciaTooltip,
	dosBloquesTooltip,
	multiClienteTooltip,
	saldoAFavorTooltip,
	saldoCobranzaTooltip,
} from '../../constants';
import { clientesOrdenados, colorVencido, usd, usdOrDash } from '../../helpers';
import { IAgingAgencia, IAgingCliente, IAgingData, IAgingTotales } from '../../types';
import { DrilldownTarget } from '../AgingDrilldown';

interface AgingTableProps {
	data: IAgingData;
	onDrilldown: (target: DrilldownTarget) => void;
}

/** Celda de importe clickeable: si es cero no se puede abrir nada. */
function Importe({
	value,
	onClick,
	color,
	bold,
}: {
	value: number;
	onClick?: () => void;
	color?: string;
	bold?: boolean;
}): JSX.Element {
	const clickable = value !== 0 && Boolean(onClick);
	return (
		<TableCell
			align="right"
			onClick={clickable ? onClick : undefined}
			sx={{
				whiteSpace: 'nowrap',
				cursor: clickable ? 'pointer' : 'default',
				color: value === 0 ? 'text.disabled' : color ?? 'text.primary',
				fontWeight: bold ? 700 : 400,
				'&:hover': clickable ? { textDecoration: 'underline' } : undefined,
			}}
		>
			{usdOrDash(value)}
		</TableCell>
	);
}

/**
 * La tabla dinámica de cartera: agencia → cliente, con los tramos de antigüedad
 * plegables por columna.
 *
 * Las columnas están separadas en dos bloques a propósito. A la izquierda lo que
 * el cliente debe (Cobranzas llama), a la derecha lo que tenemos que resolver
 * nosotros (Administración factura o imputa). Mezclarlos hace que se salga a
 * reclamar plata que en realidad ya está cobrada pero mal imputada.
 */
export function AgingTable({ data, onDrilldown }: AgingTableProps): JSX.Element {
	const [cerradas, setCerradas] = useState<Set<number>>(new Set());
	const [verVencido, setVerVencido] = useState(false);
	const [verPorVencer, setVerPorVencer] = useState(false);

	const toggleAgencia = (agencyId: number): void =>
		setCerradas((prev) => {
			const next = new Set(prev);
			if (next.has(agencyId)) next.delete(agencyId);
			else next.add(agencyId);
			return next;
		});

	const colsVencido = verVencido ? BUCKETS_VENCIDO.length : 0;
	const colsPorVencer = verPorVencer ? BUCKETS_POR_VENCER.length : 0;
	const colsDeuda = 2 + colsVencido + colsPorVencer;

	const agencias = useMemo(
		() => [...data.agencias].sort((a, b) => b.totales.vencidoTotal - a.totales.vencidoTotal),
		[data.agencias],
	);

	/**
	 * Las columnas de deuda de una fila: vencido (+ tramos) y por vencer (+ tramos).
	 * Idénticas para cliente, agencia y total, así los tramos siempre alinean.
	 */
	const deudaCells = (
		fila: Pick<IAgingCliente, 'buckets' | 'vencidoTotal' | 'porVencerTotal'>,
		scope: { passengerId?: string; agencyId?: number },
		bold?: boolean,
	): JSX.Element[] => {
		const out: JSX.Element[] = [
			<Importe
				key="vencido"
				value={fila.vencidoTotal}
				bold
				color={colorVencido(fila.buckets)}
				onClick={() =>
					onDrilldown({
						titulo: 'Facturas vencidas',
						subtitulo: scopeLabel(scope),
						bucket: 'VENCIDO',
						...scope,
					})
				}
			/>,
		];
		if (verVencido) {
			for (const b of BUCKETS_VENCIDO) {
				out.push(
					<Importe
						key={b.id}
						value={fila.buckets[b.id] ?? 0}
						bold={bold}
						color="error.main"
						onClick={() =>
							onDrilldown({
								titulo: `Vencido ${b.label}`,
								subtitulo: scopeLabel(scope),
								bucket: b.id,
								...scope,
							})
						}
					/>,
				);
			}
		}
		out.push(
			<Importe
				key="porVencer"
				value={fila.porVencerTotal}
				bold
				onClick={() =>
					onDrilldown({
						titulo: 'Facturas emitidas sin vencer',
						subtitulo: scopeLabel(scope),
						bucket: 'POR_VENCER',
						...scope,
					})
				}
			/>,
		);
		if (verPorVencer) {
			for (const b of BUCKETS_POR_VENCER) {
				out.push(
					<Importe
						key={b.id}
						value={fila.buckets[b.id] ?? 0}
						bold={bold}
						onClick={() =>
							onDrilldown({
								titulo: b.label,
								subtitulo: scopeLabel(scope),
								bucket: b.id,
								...scope,
							})
						}
					/>,
				);
			}
		}
		return out;
	};

	const scopeLabel = (scope: { passengerId?: string; agencyId?: number }): string => {
		if (scope.passengerId) {
			const cli = data.agencias.flatMap((a) => a.clientes).find((c) => c.passengerId === scope.passengerId);
			return cli?.nombre ?? scope.passengerId;
		}
		if (scope.agencyId) return agencyName(scope.agencyId);
		return 'Todas las agencias';
	};

	/** Las columnas de "tarea nuestra" son iguales para cliente, agencia y total. */
	const tareaCells = (
		t: Pick<IAgingTotales, 'pendienteFacturar' | 'saldoAFavor' | 'diferencia'>,
		scope: { passengerId?: string; agencyId?: number },
		bold?: boolean,
	): JSX.Element[] => [
		<Importe
			key="facturar"
			value={t.pendienteFacturar}
			bold={bold}
			color="info.main"
			onClick={() =>
				onDrilldown({
					titulo: 'Pendiente de facturar',
					subtitulo: scopeLabel(scope),
					tipoOrden: 'FACTURAR',
					...scope,
				})
			}
		/>,
		<Importe
			key="aFavor"
			value={t.saldoAFavor}
			bold={bold}
			color="success.main"
			onClick={() =>
				onDrilldown({
					titulo: 'Saldo a favor del cliente',
					subtitulo: scopeLabel(scope),
					tipoOrden: 'SALDO_A_FAVOR',
					...scope,
				})
			}
		/>,
		<TableCell
			key="dif"
			align="right"
			sx={{
				whiteSpace: 'nowrap',
				fontWeight: bold ? 700 : 400,
				color: t.diferencia === 0 ? 'text.disabled' : 'warning.dark',
			}}
		>
			{usdOrDash(t.diferencia)}
		</TableCell>,
	];

	return (
		<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '68vh' }}>
			<Table size="small" stickyHeader sx={{ '& td, & th': { py: 0.75 } }}>
				<TableHead>
					{/* Fila de grupos: separa "le reclamo al cliente" de "lo arreglo yo". */}
					<TableRow>
						<TableCell sx={{ bgcolor: 'background.paper' }} />
						<TableCell align="center" sx={{ bgcolor: 'background.paper' }}>
							<Typography variant="caption" color="text.secondary">
								Contexto
							</Typography>
						</TableCell>
						<TableCell align="center" colSpan={colsDeuda} sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								<Typography variant="caption" fontWeight={700}>
									DEUDA DEL CLIENTE — se reclama
								</Typography>
								<SectionInfoTooltip content={dosBloquesTooltip} />
							</Box>
						</TableCell>
						<TableCell align="center" colSpan={3} sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
							<Typography variant="caption" fontWeight={700}>
								TAREA NUESTRA — no se reclama
							</Typography>
						</TableCell>
					</TableRow>

					<TableRow>
						<TableCell sx={{ minWidth: 260, bgcolor: 'background.paper' }}>Cliente</TableCell>
						<TableCell align="right" sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								Saldo de cobranza
								<SectionInfoTooltip content={saldoCobranzaTooltip} />
							</Box>
						</TableCell>

						<TableCell align="right" sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								Vencido
								<SectionInfoTooltip content={antiguedadTooltip} />
								<Tooltip title={verVencido ? 'Ocultar tramos' : 'Ver por antigüedad'}>
									<IconButton size="small" onClick={() => setVerVencido((v) => !v)} sx={{ p: 0.2 }}>
										{verVencido ? <UnfoldLessIcon sx={{ fontSize: 15 }} /> : <UnfoldMoreIcon sx={{ fontSize: 15 }} />}
									</IconButton>
								</Tooltip>
							</Box>
						</TableCell>
						{verVencido &&
							BUCKETS_VENCIDO.map((b) => (
								<TableCell key={b.id} align="right" sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
									<Tooltip title={`Vencido hace ${b.label.toLowerCase()}`}>
										<Typography variant="caption" color="error.main" fontWeight={600}>
											{b.corto}
										</Typography>
									</Tooltip>
								</TableCell>
							))}

						<TableCell align="right" sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								Por vencer
								<Tooltip title={verPorVencer ? 'Ocultar tramos' : 'Ver por vencimiento'}>
									<IconButton size="small" onClick={() => setVerPorVencer((v) => !v)} sx={{ p: 0.2 }}>
										{verPorVencer ? <UnfoldLessIcon sx={{ fontSize: 15 }} /> : <UnfoldMoreIcon sx={{ fontSize: 15 }} />}
									</IconButton>
								</Tooltip>
							</Box>
						</TableCell>
						{verPorVencer &&
							BUCKETS_POR_VENCER.map((b) => (
								<TableCell key={b.id} align="right" sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
									<Tooltip title={b.label}>
										<Typography variant="caption" color="text.secondary" fontWeight={600}>
											{b.corto}
										</Typography>
									</Tooltip>
								</TableCell>
							))}

						<TableCell align="right" sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
							Pendiente de facturar
						</TableCell>
						<TableCell align="right" sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								Saldo a favor
								<SectionInfoTooltip content={saldoAFavorTooltip} />
							</Box>
						</TableCell>
						<TableCell align="right" sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								Diferencia
								<SectionInfoTooltip content={diferenciaTooltip} />
							</Box>
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{agencias.map((ag: IAgingAgencia) => {
						const abierta = !cerradas.has(ag.agencyId);
						const scope = { agencyId: ag.agencyId };
						return (
							<React.Fragment key={ag.agencyId}>
								<TableRow
									hover
									sx={{ bgcolor: 'action.hover', cursor: 'pointer' }}
									onClick={() => toggleAgencia(ag.agencyId)}
								>
									<TableCell>
										<Box display="flex" alignItems="center" gap={0.5}>
											{abierta ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
											<Typography variant="body2" fontWeight={700}>
												{agencyName(ag.agencyId)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{ag.totales.clientes} clientes
											</Typography>
										</Box>
									</TableCell>
									<Importe value={ag.totales.saldoCobranza} bold />
									{deudaCells(ag.totales, scope, true)}
									{tareaCells(ag.totales, scope, true)}
								</TableRow>

								{abierta &&
									clientesOrdenados(ag).map((c) => {
										const cScope = { passengerId: c.passengerId, agencyId: ag.agencyId };
										return (
											<TableRow key={c.passengerId} hover>
												<TableCell sx={{ pl: 4 }}>
													<Typography variant="body2">{c.nombre}</Typography>
													<Typography variant="caption" color="text.disabled">
														{c.documento ?? 's/documento'} · {c.ordenes} órdenes · {c.facturas} facturas
													</Typography>
												</TableCell>
												<Importe
													value={c.saldoCobranza}
													onClick={() =>
														onDrilldown({
															titulo: 'Saldo de cobranza',
															subtitulo: c.nombre,
															tipoOrden: 'COBRANZA',
															...cScope,
														})
													}
												/>
												{deudaCells(c, cScope)}
												{tareaCells(c, cScope)}
											</TableRow>
										);
									})}

								{abierta && ag.multiCliente && (
									<TableRow hover sx={{ bgcolor: 'warning.light' }}>
										<TableCell sx={{ pl: 4 }}>
											<Box display="inline-flex" alignItems="center" gap={0.5}>
												<Chip label="A definir" size="small" color="warning" sx={{ height: 18 }} />
												<Typography variant="body2">
													Órdenes con más de un cliente facturable ({ag.multiCliente.ordenes})
												</Typography>
												<SectionInfoTooltip content={multiClienteTooltip} />
											</Box>
										</TableCell>
										<Importe
											value={ag.multiCliente.saldoCobranza}
											onClick={() =>
												onDrilldown({
													titulo: 'Órdenes con más de un cliente facturable',
													subtitulo: agencyName(ag.agencyId),
													tipoOrden: 'MULTI_CLIENTE',
													agencyId: ag.agencyId,
												})
											}
										/>
										{/* Sin facturar todavía: no hay antigüedad que mostrar. */}
										<TableCell colSpan={colsDeuda} />
										<Importe value={ag.multiCliente.pendienteFacturar} />
										<Importe value={ag.multiCliente.saldoAFavor} />
										<TableCell />
									</TableRow>
								)}
							</React.Fragment>
						);
					})}

					<TableRow sx={{ bgcolor: 'grey.900', '& td': { color: 'common.white' } }}>
						<TableCell>
							<Typography variant="body2" fontWeight={700}>
								TOTAL — {data.totales.clientes} clientes
							</Typography>
						</TableCell>
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{usd(data.totales.saldoCobranza)}
						</TableCell>
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{usd(data.totales.vencidoTotal)}
						</TableCell>
						{verVencido &&
							BUCKETS_VENCIDO.map((b) => (
								<TableCell key={b.id} align="right" sx={{ fontWeight: 700 }}>
									{usdOrDash(data.totales.buckets[b.id] ?? 0)}
								</TableCell>
							))}
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{usd(data.totales.porVencerTotal)}
						</TableCell>
						{verPorVencer &&
							BUCKETS_POR_VENCER.map((b) => (
								<TableCell key={b.id} align="right" sx={{ fontWeight: 700 }}>
									{usdOrDash(data.totales.buckets[b.id] ?? 0)}
								</TableCell>
							))}
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{usd(data.totales.pendienteFacturar)}
						</TableCell>
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{usd(data.totales.saldoAFavor)}
						</TableCell>
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{usd(data.totales.diferencia)}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
}
